// ── Death Replay System ──
// Maintains a rolling buffer of recent game snapshots and plays back a
// slow-motion replay from the killer's perspective when the local player dies.
//
// Flow:
//   1. Each tick: recordSnapshot() stores a lightweight snapshot into a ring buffer
//   2. On death: onDeath(killerId, deathTick) freezes the buffer
//   3. After 0.5s delay: replay plays from 3s before death at 0.5x speed
//   4. Camera follows the KILLER's perspective (position + rotation from snapshots)
//   5. Brief flash highlights the killing shot moment
//   6. After replay ends, transitions to normal spectator mode

import { TICK_RATE } from '../core/config';

// ── Constants ──

/** Ring buffer capacity: 5 seconds at 64Hz = 320 snapshots. */
export const BUFFER_SIZE = Math.round(5 * TICK_RATE); // 320

/** Replay playback speed (slow-motion). */
export const REPLAY_SPEED = 0.5;

/** Delay in seconds after death before replay starts. */
export const REPLAY_DELAY_S = 0.5;

/** How far back (in game-seconds) from the death moment to start replay. */
export const REPLAY_LOOKBACK_S = 3.0;

/** How close to the death tick (in game-seconds) the kill flash activates. */
const KILL_FLASH_WINDOW_S = 0.2;

// ── Lightweight snapshot types ──

/** Minimal per-player state stored in the rolling buffer. */
export interface DeathReplayPlayerState {
  id: number;
  x: number;
  y: number;
  z: number;
  yaw: number;
  pitch: number;
  weapon: string;
  health: number;
}

/** One frame in the rolling buffer. */
export interface DeathReplaySnapshot {
  tick: number;
  timestamp: number;
  players: Map<number, DeathReplayPlayerState>;
}

/** Observable state exposed via getState(). */
export interface DeathReplayState {
  phase: 'idle' | 'delay' | 'playing' | 'ended';
  isReplaying: boolean;
  frozen: boolean;
  killerId: number;
  deathTick: number;
  bufferCount: number;
  killFlashActive: boolean;
  showWatermark: boolean;
  /** Current replay tick being displayed (only meaningful during 'playing'). */
  currentReplayTick: number;
}

type ReplayPhase = 'idle' | 'delay' | 'playing' | 'ended';

// ── System ──

export class DeathReplaySystem {
  // Ring buffer
  private buffer: (DeathReplaySnapshot | null)[] = new Array(BUFFER_SIZE).fill(null);
  private writeIndex = 0;
  private count = 0;
  private frozen = false;

  // Death state
  private killerId = 0;
  private deathTick = 0;

  // Replay playback
  private phase: ReplayPhase = 'idle';
  private delayTimer = 0;
  private replayElapsed = 0; // real-time elapsed since replay started playing
  private replayStartTick = 0;
  private replayEndTick = 0;
  private currentReplayTick = 0;

  // Ordered snapshot array extracted from ring buffer on death (for playback)
  private replaySnapshots: DeathReplaySnapshot[] = [];
  private replaySnapshotByTick = new Map<number, DeathReplaySnapshot>();

  // ── Public API ──

  /** Record a snapshot into the rolling buffer. Called every tick during live play. */
  recordSnapshot(snapshot: DeathReplaySnapshot): void {
    if (this.frozen) return;

    this.buffer[this.writeIndex] = snapshot;
    this.writeIndex = (this.writeIndex + 1) % BUFFER_SIZE;
    if (this.count < BUFFER_SIZE) {
      this.count++;
    }
  }

  /**
   * Called when the local player dies.
   * Freezes the buffer and begins the replay sequence.
   */
  onDeath(killerId: number, deathTick: number): void {
    // Ignore if already in a replay sequence
    if (this.phase !== 'idle') return;

    this.frozen = true;
    this.killerId = killerId;
    this.deathTick = deathTick;

    // Extract ordered snapshots from ring buffer
    this.extractReplaySnapshots();

    // Enter delay phase
    this.phase = 'delay';
    this.delayTimer = 0;
    this.replayElapsed = 0;

    // Compute replay start/end ticks
    const lookbackTicks = Math.round(REPLAY_LOOKBACK_S * TICK_RATE);
    this.replayEndTick = deathTick;

    if (this.replaySnapshots.length > 0) {
      const earliestTick = this.replaySnapshots[0].tick;
      this.replayStartTick = Math.max(earliestTick, deathTick - lookbackTicks);
    } else {
      this.replayStartTick = deathTick;
    }

    this.currentReplayTick = this.replayStartTick;
  }

  /**
   * Advance the replay system. Called every frame.
   * Returns the current replay snapshot to render, or null if not in playback.
   */
  update(dt: number): DeathReplaySnapshot | null {
    switch (this.phase) {
      case 'idle':
        return null;

      case 'delay':
        this.delayTimer += dt;
        if (this.delayTimer >= REPLAY_DELAY_S) {
          this.phase = 'playing';
          this.replayElapsed = 0;
          this.currentReplayTick = this.replayStartTick;
        }
        return null;

      case 'playing': {
        // Advance replay time (real-time dt * replay speed = game-time advancement)
        this.replayElapsed += dt;
        const gameTimeElapsed = this.replayElapsed * REPLAY_SPEED;
        const tickAdvance = Math.floor(gameTimeElapsed * TICK_RATE);
        this.currentReplayTick = this.replayStartTick + tickAdvance;

        // Check if we've reached the end
        if (this.currentReplayTick >= this.replayEndTick) {
          this.currentReplayTick = this.replayEndTick;
          this.phase = 'ended';
        }

        // Find the closest snapshot
        return this.getSnapshotAtTick(this.currentReplayTick);
      }

      case 'ended':
        return null;
    }
  }

  /** Skip the replay immediately. */
  skipReplay(): void {
    if (this.phase === 'idle') return;
    this.phase = 'ended';
  }

  /**
   * BUG-P-7: Re-trigger the most recent death replay (mobile "Replay" button on
   * the death screen). No-op if there is no recorded replay (e.g. the player
   * has not died yet, or `reset()` has wiped buffer state). The `playerId`
   * argument is the local player whose POV is being re-watched — currently
   * informational; replay POV is always the killer captured on the original
   * `onDeath()` call.
   */
  play(_playerId: number): void {
    if (this.replaySnapshots.length === 0) return;
    // Restart playback from the beginning of the captured window.
    this.phase = 'delay';
    this.delayTimer = 0;
    this.replayElapsed = 0;
    this.currentReplayTick = this.replayStartTick;
  }

  /** Reset the system entirely — call after transitioning to spectator mode. */
  reset(): void {
    this.buffer = new Array(BUFFER_SIZE).fill(null);
    this.writeIndex = 0;
    this.count = 0;
    this.frozen = false;
    this.killerId = 0;
    this.deathTick = 0;
    this.phase = 'idle';
    this.delayTimer = 0;
    this.replayElapsed = 0;
    this.replayStartTick = 0;
    this.replayEndTick = 0;
    this.currentReplayTick = 0;
    this.replaySnapshots = [];
    this.replaySnapshotByTick.clear();
  }

  /** Get current observable state (for UI rendering / tests). */
  getState(): DeathReplayState {
    const isPlaying = this.phase === 'playing';

    // Kill flash is active when we're near the death tick during playback
    let killFlashActive = false;
    if (isPlaying) {
      const ticksFromDeath = this.deathTick - this.currentReplayTick;
      const secondsFromDeath = ticksFromDeath / TICK_RATE;
      killFlashActive = secondsFromDeath >= 0 && secondsFromDeath <= KILL_FLASH_WINDOW_S;
    }

    return {
      phase: this.phase,
      isReplaying: isPlaying,
      frozen: this.frozen,
      killerId: this.killerId,
      deathTick: this.deathTick,
      bufferCount: this.count,
      killFlashActive,
      showWatermark: isPlaying,
      currentReplayTick: this.currentReplayTick,
    };
  }

  /**
   * Get all snapshots currently in the buffer (ordered oldest to newest).
   * Useful for testing.
   */
  getBufferSnapshots(): DeathReplaySnapshot[] {
    return this.getOrderedSnapshots();
  }

  // ── Internal ──

  /** Extract snapshots from ring buffer into ordered array for playback. */
  private extractReplaySnapshots(): void {
    this.replaySnapshots = this.getOrderedSnapshots();
    this.replaySnapshotByTick.clear();
    for (const snap of this.replaySnapshots) {
      this.replaySnapshotByTick.set(snap.tick, snap);
    }
  }

  /** Read all valid snapshots from ring buffer in chronological order. */
  private getOrderedSnapshots(): DeathReplaySnapshot[] {
    const result: DeathReplaySnapshot[] = [];

    if (this.count === 0) return result;

    // Start reading from the oldest entry
    const startIdx = this.count < BUFFER_SIZE
      ? 0
      : this.writeIndex; // writeIndex points to the next-to-be-overwritten (oldest)

    for (let i = 0; i < this.count; i++) {
      const idx = (startIdx + i) % BUFFER_SIZE;
      const snap = this.buffer[idx];
      if (snap !== null) {
        result.push(snap);
      }
    }

    return result;
  }

  /** Find the snapshot closest to the given tick. */
  private getSnapshotAtTick(tick: number): DeathReplaySnapshot | null {
    // Direct hit
    const exact = this.replaySnapshotByTick.get(tick);
    if (exact) return exact;

    // Find closest by scanning (snapshots are ordered)
    if (this.replaySnapshots.length === 0) return null;

    let best = this.replaySnapshots[0];
    let bestDist = Math.abs(best.tick - tick);

    for (let i = 1; i < this.replaySnapshots.length; i++) {
      const dist = Math.abs(this.replaySnapshots[i].tick - tick);
      if (dist < bestDist) {
        best = this.replaySnapshots[i];
        bestDist = dist;
      } else {
        // Since sorted, distance will only increase from here
        break;
      }
    }

    return best;
  }
}
